import React, { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../auth/AuthContext";
import { addPaymentFromShow, AddQuickPaymentPayload, addQuickPayment } from "../services/bookings";
import BackButton from "./calendar/BackButton";
import { Customer } from "../types/customer";
import GlobalLoader from "./ui/GlobalLoader";
import { motion } from "framer-motion";
import { Phone, Mail, Clock, User, ChevronRight, CreditCard, Receipt } from "lucide-react";
import { cn } from "../lib/utils";

interface Service {
  id: string;
  name: string;
  duration: string;
  price: string;
}

interface PaymentStepProps {
  selectedServices: Service[];
  selectedCustomer: Customer;
  appointmentTime: string;
  tip: number;
  setTip: React.Dispatch<React.SetStateAction<number>>;
  goToFinalPayment: () => void;
  bookingId?: number;
  goBack: () => void;
  staffList: { id: number; name: string; color: string }[];
  stylist: string;
}

const PaymentStep: React.FC<PaymentStepProps> = ({
  selectedServices,
  selectedCustomer,
  appointmentTime,
  tip,
  setTip,
  goToFinalPayment,
  bookingId,
  goBack,
  staffList,
  stylist,
}) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const [selectedTip, setSelectedTip] = useState<number>(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStylist, setSelectedStylist] = useState<string>(stylist);

  console.log("BOKING ID", bookingId);

  const products = [{ name: "Premium Hair Wax", price: 35 }];

  const basePrice = selectedServices.reduce((acc, s) => acc + Number(s.price), 0);

  const tipOptions = [
    { percentage: 0, label: "No Tip", amount: 0 },
    { percentage: 5, label: "5%", amount: +(basePrice * 0.05).toFixed(2) },
    { percentage: 10, label: "10%", amount: +(basePrice * 0.1).toFixed(2) },
    { percentage: 15, label: "15%", amount: +(basePrice * 0.15).toFixed(2) },
    { percentage: 20, label: "20%", amount: +(basePrice * 0.2).toFixed(2) },
    { percentage: 30, label: "30%", amount: +(basePrice * 0.3).toFixed(2) },
  ];

  const totalAmount =
    basePrice +
    (tipOptions.find((t) => t.percentage === selectedTip)?.amount || 0);
  const paidAmount = 35;
  const dueAmount = totalAmount - paidAmount;

  const getInitials = (name: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || "??";
  };

  const handleSavePayment = async () => {
    if (!bookingId) {
      setError("Booking ID not found");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await addPaymentFromShow(bookingId);
      console.log("Payment API response:", response);
      goToFinalPayment(); // Move to next step after payment is created
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to add payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full bg-[#F9F9F9] overflow-hidden">
      {/* LEFT PANEL - Customer Profile */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="w-[35%] bg-white border-r border-gray-100 flex flex-col p-8 overflow-y-auto"
      >
        <div className="flex flex-col items-center text-center space-y-4 mb-10">
          <div className="w-24 h-24 rounded-full bg-[#885ABB]/10 flex items-center justify-center text-[#885ABB] text-3xl font-bold shadow-sm border-4 border-white">
            {getInitials(selectedCustomer.name)}
          </div>
          <div>
            <h3 className="text-xl font-[600] text-[#402B69] mb-1">{selectedCustomer.name}</h3>
            <p className="text-sm text-gray-400 font-medium">{selectedCustomer.email || "No email provided"}</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-3">
            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Contact Info</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 text-sm text-gray-500 font-medium">
                <Phone size={14} className="text-[#885ABB]" /> <span>{selectedCustomer.phone || "Not provided"}</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 text-sm text-gray-500 font-medium">
                <Mail size={14} className="text-[#885ABB]" /> <span>{selectedCustomer.email || "Not provided"}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Appointment Details</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 rounded-xl bg-[#885ABB]/5 text-sm text-[#885ABB] font-bold">
                <div className="flex items-center gap-3">
                  <Clock size={14} /> <span>{appointmentTime}</span>
                </div>
                <span className="text-[10px] uppercase">Slot</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Right Side: Appointment Details */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex-1 bg-[#F9F9F9] flex flex-col overflow-hidden relative"
      >
        {/* Internal Header for Right Panel */}
        <div className="px-8 pt-8 pb-5 bg-white border-b border-gray-100 shadow-sm relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[22px] font-[600] text-[#402B69]">Payment Checkout</h2>
              <p className="text-[10px] text-gray-400 mt-0.5 font-semibold uppercase">
                Step 4 of 4: Finalize & Pay
              </p>
            </div>
            <BackButton onClick={goBack} />
          </div>

          <div className="flex items-center gap-1.5">
            {[0, 1, 2, 3].map((s) => (
              <div
                key={s}
                className={cn(
                  "h-1 flex-1 rounded-full transition-all duration-500",
                  s <= 3 ? "bg-[#885ABB]" : "bg-gray-100"
                )}
              />
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-8 p-8 pr-2 custom-scrollbar">
          {/* Stylist & Time Quick View */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#885ABB]/5 flex items-center justify-center text-[#885ABB]">
                <User size={20} />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Assigned Stylist</p>
                <Select value={selectedStylist} onValueChange={setSelectedStylist}>
                  <SelectTrigger className="h-6 p-0 border-none shadow-none text-[15px] font-[600] text-[#402B69] focus:ring-0 bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-100 rounded-xl shadow-xl">
                    {staffList.map((s) => (
                      <SelectItem key={s.id} value={s.name} className="text-xs font-bold">{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1 text-right">Scheduled Time</p>
              <div className="flex items-center gap-2 text-[15px] font-bold text-[#885ABB]">
                <Clock size={16} />
                <span>{appointmentTime}</span>
              </div>
            </div>
          </div>

          <section className="space-y-4">
            <div className="flex justify-between items-center px-1">
              <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Billing Breakdown</h4>
              <span className="text-[10px] font-bold text-[#885ABB] bg-[#885ABB]/5 px-2.5 py-1 rounded-full border border-[#885ABB]/10">Detailed Review</span>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3 font-bold text-[11px] text-gray-400">
                  <Receipt size={14} className="text-[#885ABB]" />
                  <span className="uppercase tracking-widest">Services & Products</span>
                </div>
              </div>

              <div className="p-6 space-y-5">
                {selectedServices.map((service, idx) => (
                  <div key={idx} className="flex justify-between items-start">
                    <div className="flex gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#885ABB] mt-1.5" />
                      <div>
                        <p className="text-[14px] font-[600] text-[#402B69] capitalize">{service.name}</p>
                        <p className="text-[11px] text-gray-400 font-bold uppercase">{service.duration}</p>
                      </div>
                    </div>
                    <span className="text-[14px] font-bold text-[#402B69]">{currencySymbol}{Number(service.price).toFixed(2)}</span>
                  </div>
                ))}

                {products.map((product, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <div className="flex gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5" />
                      <p className="text-[14px] font-[600] text-[#402B69]">{product.name}</p>
                    </div>
                    <span className="text-[14px] font-bold text-[#402B69]">{currencySymbol}{product.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="p-6 bg-[#F9F9F9] border-t border-gray-100 space-y-3">
                <div className="flex justify-between text-[13px] font-medium text-gray-500">
                  <span>Subtotal</span>
                  <span>{currencySymbol}{totalAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[13px] text-red-500 font-medium">
                  <span>Amount Paid (Deposit)</span>
                  <span>- {currencySymbol}{paidAmount.toFixed(2)}</span>
                </div>
                <div className="pt-5 border-t border-gray-200/60 flex justify-between items-center">
                  <span className="text-[18px] font-[600] text-[#402B69]">Remaining Due</span>
                  <span className="text-3xl font-extrabold text-[#885ABB]">{currencySymbol}{dueAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Internal Note</h4>
            <textarea
              placeholder="Add a private note about this transaction..."
              className="w-full h-24 p-4 bg-white border border-gray-100 rounded-2xl text-[14px] font-medium resize-none focus:outline-none focus:ring-2 focus:ring-[#885ABB]/10 transition-all placeholder:text-gray-300 shadow-sm"
            />
          </section>
        </div>

        {/* Sticky Action Footer */}
        <div className="p-6 bg-white border-t border-gray-50 flex gap-4">
          <button
            className="flex-1 h-12 bg-white border border-gray-200 text-[#402B69] rounded-xl font-bold hover:bg-gray-50 transition-all text-sm shadow-sm"
          >
            Save Draft
          </button>

          <button
            onClick={handleSavePayment}
            disabled={loading}
            className="flex-[2] h-12 bg-[#885ABB] text-white rounded-xl font-bold hover:bg-[#7748A4] shadow-lg shadow-[#885ABB]/10 transition-all flex items-center justify-center gap-2 group text-sm disabled:opacity-50"
          >
            <CreditCard size={18} />
            <span>{loading ? "Processing..." : "Continue to pay"}</span>
            <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="absolute bottom-24 right-8 bg-red-50 text-red-500 px-4 py-3 rounded-xl text-xs font-bold border border-red-100 shadow-lg animate-in fade-in slide-in-from-bottom-2">
            {error}
          </div>
        )}
      </motion.div>

      {loading && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
          <GlobalLoader
            initialPageLoad={false}
            description="Processing payment..."
            compensateSidebar={false}
          />
        </div>
      )}
    </div>
  );
};

export default PaymentStep;
